import React, { useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Provider, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import store from '../store';

import StatusView from '../map/StatusView';
import StartsStopsView from "../map/StartsStopsView";
import {ThemeProvider} from "@material-ui/core/styles";
import {useTheme} from "@material-ui/core";

const getStatusColor = (status) => {
    switch (status) {
        case 'online':
            return 'green';
        case 'offline':
            return 'gray';
        case 'slowing':
            return 'yellow';
        default:
            return 'red';
    }
};


const PositionsMapGoogle = ({ map, googleMapsApi, icons, positions, replayBack }) => {
    const markersRef = useRef({});
    const theme = useTheme();
    const infoWindowRef = useRef(null);
    const history = useHistory();
    const devices = useSelector((state) => state.devices.items);
    const currentPos  = useRef(null)
    const mapType = useSelector(state => state.session.user && state.session.user.map)


    const handleMarkerClick = useCallback((marker, deviceId, device) => {
        if (!map || !googleMapsApi || !infoWindowRef.current) return;

        const placeholder = document.createElement('div');
        ReactDOM.render(
                <Provider store={store}>
                    {!replayBack ?
                        <ThemeProvider theme={theme}>
                            <StatusView
                                mapGoogle={map}
                                deviceId={deviceId}
                                onShowDetails={(positionId) => history.push(`/position/${positionId}`)}
                                history={history}
                            />
                        </ThemeProvider>
                        :
                        <ThemeProvider theme={theme}>
                            <StartsStopsView position={currentPos.current} status={"positionFixTime"} deviceName={device.name} mapType={mapType}/>
                        </ThemeProvider>
                    }
                </Provider>,
            placeholder
        );

        infoWindowRef.current.setContent(placeholder);
        infoWindowRef.current.open(map, marker);

        googleMapsApi.event.addListenerOnce(infoWindowRef.current, 'closeclick', () => {
            ReactDOM.unmountComponentAtNode(placeholder);
        });
    }, [history, theme]);

    useEffect(() => {
        let mapClickListener;
        if (map && googleMapsApi) {
            if (!infoWindowRef.current) {
                infoWindowRef.current = new googleMapsApi.InfoWindow();
            }
            mapClickListener = map.addListener('click', () => {
                if (infoWindowRef.current) {
                    const currentContent = infoWindowRef.current.getContent();
                    if (currentContent instanceof HTMLElement) ReactDOM.unmountComponentAtNode(currentContent);
                    infoWindowRef.current.close();
                }
            });
            return () => {
                if (mapClickListener) {
                    googleMapsApi.event.removeListener(mapClickListener);
                }
                if (infoWindowRef.current) {
                    const currentContent = infoWindowRef.current.getContent();
                    if (currentContent instanceof HTMLElement) ReactDOM.unmountComponentAtNode(currentContent);
                    infoWindowRef.current.close();
                }
            };
        }
    }, [map, googleMapsApi, theme]);

    const fitMapToMarkers = useCallback(() => {
        const allMarkers = Object.values(markersRef.current);
        if (allMarkers.length > 0 && map && map.fitBounds) {
            const bounds = new googleMapsApi.LatLngBounds();
            allMarkers.forEach((marker) => bounds.extend(marker.getPosition()));
            map.fitBounds(bounds);
            googleMapsApi.event.addListenerOnce(map, 'idle', () => {
                if (map.getZoom() > 12) {
                    map.setZoom(12);
                }
            });
        }
    }, [map, googleMapsApi, theme]);

    useEffect(() => {
        if (!map || !googleMapsApi || !icons || Object.keys(icons).length === 0 || !positions) {
            return;
        }

        const newMarkers = [];

        positions.forEach((position) => {
            const device = devices[position.deviceId];
            if (!device) return;
            currentPos.current = position;

            const deviceId = position.deviceId;
            const deviceName = device.name || `Device ID: ${deviceId}`;
            const deviceCategory = device.category || 'default';
            const deviceStatus = device.status;
            const statusColor = getStatusColor(deviceStatus);
            const iconKey = `${deviceCategory}-${statusColor}`;
            const markerIcon = icons[iconKey];

            if (!markerIcon) console.warn(`Icon not found for key: ${iconKey}. Using default for ${deviceName}.`);

            if (typeof position.latitude !== 'number' || typeof position.longitude !== 'number') {
                console.warn(`Invalid coordinates for device: ${deviceName}`, position);
                return;
            }

            const positionLatLon = new googleMapsApi.LatLng(position.latitude, position.longitude);

            if (markersRef.current[deviceId]) {
                markersRef.current[deviceId].setPosition(positionLatLon);
                if (markersRef.current[deviceId].getIcon() !== markerIcon) {
                    markersRef.current[deviceId].setIcon(markerIcon);
                }
            } else {
                const marker = new googleMapsApi.Marker({
                    position: positionLatLon,
                    map,
                    icon: markerIcon,
                    title: deviceName,
                    deviceId: deviceId,
                });
                marker.addListener('click', () => handleMarkerClick(marker, deviceId, device));
                markersRef.current[deviceId] = marker;
                newMarkers.push(marker);
            }
        });

        if (newMarkers.length > 0 && !replayBack) fitMapToMarkers();
    }, [map, googleMapsApi, icons, positions, devices, handleMarkerClick, theme]);

    useEffect(() => () => {
        Object.values(markersRef.current).forEach((marker) => {
            googleMapsApi.event.clearInstanceListeners(marker);
            marker.setMap(null);
        });
        markersRef.current = {};
        if (infoWindowRef.current) {
            infoWindowRef.current.close();
        }
    }, [theme]);

    useEffect(() => {
        const styleId = 'google-maps-cyber-popup';
        let styleElement = document.getElementById(styleId);

        if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = styleId;
            document.head.appendChild(styleElement);
        }

        const isDarkMode = theme.palette.typeBackground === 'dark';
        const popupBg = isDarkMode ? 'rgba(15, 25, 35, 0.7)' : 'rgba(255, 255, 255, 0.5)';
        const glowOpacity = isDarkMode ? '0.4' : '0.2';

        styleElement.innerHTML = `
    /* Main wrapper container override */
    .gm-style-iw-c {
      background: ${popupBg} !important;
      backdrop-filter: blur(10px) !important;
      -webkit-backdrop-filter: blur(10px) !important;
      border: 1px solid #00f2ff !important;
      box-shadow: 0 0 20px rgba(0, 242, 255, ${glowOpacity}) !important;
      border-radius: 20px !important;
      padding: 0 !important;
    }

    /* Target inner content block container */
    .gm-style-iw-d {
      overflow: hidden !important;
      padding: 0 !important;
      max-height: none !important;
    }

    /* Google Maps native close button container */
    .gm-style-iw-chr {
      position: absolute !important;
      right: 0;
      top: 0;
      z-index: 1;
    }

    /* Close Button Wrapper */
    .gm-style-iw-chr > button.gm-ui-hover-effect {
     flex: 0 0 50% !important; /* Combines grow, shrink, and basis */
     height: 15px !important;
      margin: 4px 4px 0 0 !important;
     padding: 4px !important;
     outline: none !important;
     color: #00f2ff !important;
     opacity: 0.8 !important;
     transition: background-color 0.2s ease;
    }
   /* Close Button Hover */
   .gm-style-iw-chr > button.gm-ui-hover-effect:hover {
     background-color: rgba(0, 242, 255, 0.1) !important;
     border-radius: 50% !important;
     opacity: 1 !important;
    }
    
    /* Inner 'X' Icon Graphic */
    .gm-style-iw-chr > button.gm-ui-hover-effect > span {
     width: 100% !important;
    height: 100% !important;
     margin: 0 !important;
     background-color: #00f2ff !important;
     mask-repeat: no-repeat !important;
     mask-position: right !important; 
    }

    /* Target the popup tail/arrow pointer */
    .gm-style-iw-tc::after {
      background: ${popupBg} !important;
      backdrop-filter: blur(10px) !important;
      -webkit-backdrop-filter: blur(10px) !important;
      border: 1px solid #00f2ff !important;
      /* Mask border rules so it seamlessly joins the box border */
      border-top: none !important;
      border-left: none !important;
      box-shadow: 7px 7px 15px rgba(0, 242, 255, ${glowOpacity}) !important;
    }
  `;

        return () => {
            // Optional: if (styleElement) styleElement.remove();
        };
    }, [theme]);

    return null;
};

export default PositionsMapGoogle;